import { AnyAction, ActionCreator, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { handleErrorResponse } from '../../api/ApiErrorsHandler';
import * as LocalStorage from '../../common/LocalStorageService';
import { formLoaded } from '../form/formActions';
import { getHash } from '../form/formSelectors';
import { IAppState, LanguageLoadingStatus, ThunkMiddlware } from '../types';
import {
  loadSupportedLanguages,
  redirectToFinalScreenIfNeeded,
  setupAndGetCountry,
  loadCommonTranslations,
} from '../common/commonHelpers';

import { getCountry, getLanguage, getLanguages } from './localizationSelectors';
import {
  getMockCommonTranslations,
  getMockSupportedLanguagesResponse,
} from '../../api/Mocks';
import {
  setCommonTranslations,
  setCountry,
  setLanguage,
  setLanguageLoadingStatus,
  setLanguages,
} from './localizationActions';
import { getLanguageFromUrl, removeLangFromUrl } from '../../common/helpers';
import { DEFAULT_COUNTRY, DEFAULT_LANGUAGE } from '../../const/defaults';
import { Logger } from '../../common/logger';
import { pickSupportedLanguageKey } from './languageMatching';

const pickInitialLanguage = (getState: () => IAppState): string => {
  Logger.log(`pickInitialLanguage`);
  const queryLanguage = getLanguageFromUrl();
  const storedLanguage = LocalStorage.getItem(LocalStorage.LanguageKey);
  const stateLanguage = getLanguage(getState());

  if (queryLanguage) {
    Logger.log(`pickInitialLanguage - use lang from URL`, queryLanguage);
  } else if (storedLanguage) {
    Logger.log(`pickInitialLanguage - use stored lang`, storedLanguage);
  } else if (stateLanguage) {
    Logger.log(`pickInitialLanguage - use state lang`, stateLanguage);
  } else {
    Logger.log(`pickInitialLanguage - use default language`);
  }
  let finalLanguage =
    queryLanguage || storedLanguage || stateLanguage || DEFAULT_LANGUAGE;

  const languages = getLanguages(getState());
  if (languages.length > 0) {
    finalLanguage = pickSupportedLanguageKey(finalLanguage, languages);
  }

  return finalLanguage;
};

export const setInitialLanguage: ActionCreator<
  ThunkAction<Promise<void>, IAppState, ThunkMiddlware, AnyAction>
> = () => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  Logger.log(`setInitialLanguage`);
  const language = pickInitialLanguage(getState);
  dispatch(setLanguage(language));
};

export const changeLanguage: ActionCreator<
  ThunkAction<Promise<void>, IAppState, ThunkMiddlware, AnyAction>
> =
  (language: string) =>
  async (
    dispatch: Dispatch<any>,
    getState: () => IAppState,
    { registrationApi }
  ) => {
    Logger.log(`changeLanguage`);
    const state = getState();

    LocalStorage.setItem(LocalStorage.LanguageKey, language);
    dispatch(setLanguage(language));

    removeLangFromUrl();

    const country = getCountry(state);
    loadCommonTranslations(dispatch, country, language);

    const hash = getHash(state);
    if (hash) {
      try {
        const response = await registrationApi.getFields({ hash, language });

        if (response) {
          dispatch(formLoaded(response));
          await redirectToFinalScreenIfNeeded(dispatch, getState);
        }
      } catch (e) {
        handleErrorResponse(e, dispatch);
      }
    }
  };

export const initLanguages: ActionCreator<any> =
  () => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
    Logger.log(`initLanguages`);
    try {
      const country = await setupAndGetCountry(dispatch, getState);

      await loadSupportedLanguages(dispatch, country);
      const language = pickInitialLanguage(getState);
      dispatch(setInitialLanguage(language));
      loadCommonTranslations(dispatch, country, language);
    } catch (err) {
      dispatch(setLanguages(getMockSupportedLanguagesResponse().languages));
      dispatch(setLanguageLoadingStatus(LanguageLoadingStatus.READY));
      dispatch(setCountry(DEFAULT_COUNTRY));
      dispatch(setLanguage(DEFAULT_LANGUAGE));
      dispatch(setCommonTranslations(getMockCommonTranslations()));
    }
  };
